"use client";

import { useRef, useState, type ReactNode } from "react";
import { Link } from "@/i18n/navigation";
import RichDescription from "@/app/components/RichDescription";
import { imageUrl } from "@/lib/image-url";
import CardImage from "@/app/components/CardImage";
import { displayName } from "@/lib/display-name";

const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";

export interface CardInfo {
  id: string;
  name: string;
  description: string;
  type: string;
  rarity: string;
  cost: number;
  color: string;
  image_url: string | null;
}

export interface RelicInfo {
  id: string;
  name: string;
  description: string;
  rarity: string;
  image_url: string | null;
}

export interface PotionInfo {
  id: string;
  name: string;
  description: string;
  rarity: string;
  image_url: string | null;
}

export { cleanId, displayName } from "@/lib/display-name";

export function CardPill({
  cardId,
  upgraded,
  enchantment,
  cardData,
  bp,
  className,
  children,
}: {
  cardId: string;
  upgraded?: boolean;
  enchantment?: string;
  cardData: Record<string, CardInfo>;
  bp: string;
  className?: string;
  children?: ReactNode;
}) {
  const [show, setShow] = useState(false);
  const [above, setAbove] = useState(true);
  const ref = useRef<HTMLAnchorElement>(null);
  const info = cardData[cardId];
  return (
    <Link
      ref={ref}
      href={`${bp}/cards/${cardId.toLowerCase()}`}
      className={`relative ${className || ""}`}
      onMouseEnter={() => {
        // Flip the card preview below when there isn't room above (small
        // screens / cards near the top, e.g. the deck modal on the live page).
        setAbove((ref.current?.getBoundingClientRect().top ?? 999) > 240);
        setShow(true);
      }}
      onMouseLeave={() => setShow(false)}
    >
      {children ?? (
        <>
          {info?.name || displayName(`CARD.${cardId}`)}
          {upgraded && "+"}
          {enchantment && (
            <span className="text-[var(--color-necrobinder)] ml-1">
              [{displayName(`ENCHANTMENT.${enchantment}`)}]
            </span>
          )}
        </>
      )}
      {show && (
        // Pop the full rendered card (enchanted and/or upgraded variant when
        // the run says so) instead of the text tooltip. Falls back from the
        // enchanted render to the plain one, then to the beta render (cards
        // that only exist on the beta channel yet), then to the portrait art.
        <span
          className={`pointer-events-none absolute z-50 left-1/2 w-40 -translate-x-1/2 ${
            above ? "bottom-full mb-2" : "top-full mt-2"
          }`}
        >
          <CardImage
            id={cardId}
            upgraded={!!upgraded}
            enchantment={enchantment}
            art={info?.image_url}
            className="w-40 h-auto drop-shadow-[0_8px_24px_rgba(0,0,0,0.7)]"
          />
        </span>
      )}
    </Link>
  );
}

export function RelicPill({
  relicId,
  relicData,
  bp,
  className,
  children,
}: {
  relicId: string;
  relicData: Record<string, RelicInfo>;
  bp: string;
  className?: string;
  children?: ReactNode;
}) {
  const [show, setShow] = useState(false);
  const info = relicData[relicId];
  return (
    <Link
      href={`${bp}/relics/${relicId.toLowerCase()}`}
      className={`relative ${className || ""}`}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children ?? info?.name ?? displayName(`RELIC.${relicId}`)}
      {show && info && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-3 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] shadow-xl pointer-events-none">
          <div className="flex items-start gap-2 mb-1.5">
            {info.image_url && (
              <img
                src={imageUrl(info.image_url)}
                alt=""
                className="w-8 h-8 object-contain"
                crossOrigin="anonymous"
              />
            )}
            <div className="min-w-0">
              <div className="font-semibold text-xs text-[var(--text-primary)] truncate">
                {info.name}
              </div>
              <div className="text-[10px] text-[var(--text-muted)]">
                {info.rarity}
              </div>
            </div>
          </div>
          <div className="text-[10px] text-[var(--text-secondary)] leading-relaxed">
            <RichDescription text={info.description} />
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 bg-[var(--bg-card)] border-r border-b border-[var(--border-subtle)] rotate-45 -mt-1" />
        </div>
      )}
    </Link>
  );
}

export function PotionPill({
  potionId,
  potionData,
  bp,
  className,
  children,
}: {
  potionId: string;
  potionData: Record<string, PotionInfo>;
  bp: string;
  className?: string;
  children?: ReactNode;
}) {
  const [show, setShow] = useState(false);
  const info = potionData[potionId];
  return (
    <Link
      href={`${bp}/potions/${potionId.toLowerCase()}`}
      className={`relative ${className || ""}`}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children ?? info?.name ?? displayName(`POTION.${potionId}`)}
      {show && info && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-3 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] shadow-xl pointer-events-none">
          <div className="flex items-start gap-2 mb-1.5">
            {info.image_url && (
              <img
                src={imageUrl(info.image_url)}
                alt=""
                className="w-8 h-8 object-contain"
                crossOrigin="anonymous"
              />
            )}
            <div className="min-w-0">
              <div className="font-semibold text-xs text-[var(--text-primary)] truncate">
                {info.name}
              </div>
              <div className="text-[10px] text-[var(--text-muted)]">
                {info.rarity}
              </div>
            </div>
          </div>
          <div className="text-[10px] text-[var(--text-secondary)] leading-relaxed">
            <RichDescription text={info.description} />
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 bg-[var(--bg-card)] border-r border-b border-[var(--border-subtle)] rotate-45 -mt-1" />
        </div>
      )}
    </Link>
  );
}
